import { cn } from '@/shared/lib/cn';
import type { NavArea, NavEntry, NavSearch } from './adminNavigation';
import { NavCount, NavEntryLink } from './NavEntryLink';
import { activeEntryIn, type ActivePlace } from './navVisibility';
import type { NavCounts } from './useNavCounts';

interface SidebarAreaListProps {
  areas: NavArea[];
  platform: NavEntry[];
  place: ActivePlace;
  path: string;
  search: NavSearch;
  counts: NavCounts;
  onNavigate?: () => void;
}

const HOT = new Set(['revisionHolds']);

function Count({ entry, area, counts }: { entry?: NavEntry; area?: NavArea; counts: NavCounts }) {
  const key = entry?.count ?? area?.count;
  const value = key ? counts[key] : undefined;
  return value ? <NavCount value={value} hot={HOT.has(key ?? '')} /> : null;
}

/** Ausgeklappte Seitenleiste: Bereiche mit ihren Unterpunkten, darunter „Plattform · nur du“. */
export function SidebarAreaList({
  areas,
  platform,
  place,
  path,
  search,
  counts,
  onNavigate,
}: SidebarAreaListProps) {
  const activeAreaId = place?.kind === 'area' ? place.area.id : null;
  return (
    <>
      <ul className="flex flex-col gap-0.5">
        {areas.map((area) => {
          const active = area.id === activeAreaId;
          const first = area.entries[0] as NavEntry;
          const activeSub = active ? activeEntryIn(area.entries, path, search) : null;
          return (
            <li key={area.id} className="flex flex-col">
              <NavEntryLink
                entry={first}
                active={active && area.entries.length === 1}
                onNavigate={onNavigate}
                className={cn(
                  'rounded-control flex h-[42px] items-center gap-3 px-3 text-sm font-semibold',
                  active ? 'bg-ink text-white' : 'text-ink hover:bg-card-alt',
                )}
              >
                <area.icon size={19} aria-hidden />
                <span className="flex-1">{area.label}</span>
                <Count area={area} counts={counts} />
              </NavEntryLink>
              {area.entries.length > 1 && (
                <ul className="border-hairline mb-1 ml-[30px] flex flex-col border-l pl-3">
                  {area.entries.map((entry) => (
                    <li key={`${entry.to}${JSON.stringify(entry.search ?? {})}`}>
                      <NavEntryLink
                        entry={entry}
                        active={entry === activeSub}
                        onNavigate={onNavigate}
                        className={cn(
                          'flex min-h-[30px] items-center gap-2 text-[13px]',
                          entry === activeSub
                            ? 'text-ink font-bold'
                            : 'text-ink-soft hover:text-ink',
                        )}
                      >
                        <span className="flex-1">{entry.label}</span>
                        <Count entry={entry} counts={counts} />
                      </NavEntryLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {platform.length > 0 && (
        <div className="border-line mt-2 flex flex-col gap-0.5 border-t border-dashed pt-2.5">
          <p className="text-ink-soft px-3 pb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
            Plattform · nur du
          </p>
          {platform.map((entry) => {
            const active = place?.kind === 'platform' && place.entry === entry;
            return (
              <NavEntryLink
                key={entry.to}
                entry={entry}
                active={active}
                onNavigate={onNavigate}
                className={cn(
                  'rounded-control flex h-10 items-center px-3 text-sm font-semibold',
                  active ? 'bg-ink text-white' : 'text-ink hover:bg-card-alt',
                )}
              >
                {entry.label}
              </NavEntryLink>
            );
          })}
        </div>
      )}
    </>
  );
}
